/**
 * Hook to find the user's active challenge (first one they're participating in)
 */
import { useQuery } from '@tanstack/react-query';
import { useBackend } from './useBackend';
import {
  useCurrentIndividualChallenges,
  useCurrentRouteChallenges,
} from './useChallengeQueries';
import { RouteChallenge } from '../Model/Engagement/RouteChallenge/RouteChallenge';
import { IndividualChallenge } from '../Model/Engagement/IndividualChallenge/IndividualChallenge';
import { createIndividualChallengeParticipant } from '../Model/Engagement/IndividualChallenge/IndividualChallengeParticipant';
import { createRouteChallengeTeam } from '../Model/Engagement/RouteChallenge/RouteChallengeTeam';

export type ActiveChallenge = {
  challenge: RouteChallenge | IndividualChallenge;
  type: 'route' | 'individual';
  myProgress?: number;
  totalGoal?: number;
  rank?: number;
};

export function useActiveChallenge() {
  const backend = useBackend();
  const { data: individualChallenges = [], isLoading: isLoadingIndividual } = useCurrentIndividualChallenges();
  const { data: routeChallenges = [], isLoading: isLoadingRoute } = useCurrentRouteChallenges();

  return useQuery({
    queryKey: ['active-challenge'],
    queryFn: async (): Promise<ActiveChallenge | null> => {
      const activeIndividualChallenges = individualChallenges.filter((c) => (!c.ended || c.displayEndDateTime > new Date()) && c.displayStartDateTime < new Date());
      

      for (const challenge of activeIndividualChallenges) {
        try {
          const json = await backend.get(
            `/coach-program/engagement/individual-challenges/${challenge.id}/my-participation`,
          );
          if (json) {
            const participation = createIndividualChallengeParticipant(json);
            if (participation.active && !participation.challengeEnded) {
              return {
                challenge,
                type: 'individual',
                myProgress: participation.totalCollectedPoints,
                totalGoal: challenge.totalPossiblePoints,
                rank: participation.rank,
              };
            }
          }
          return {
            challenge,
            type: 'individual',
            myProgress: 0,
            totalGoal: challenge.totalPossiblePoints,
            rank: undefined,
          }
        } catch (err) {
          console.error('Error checking individual challenge participation:', err);
        }
      }

      const activeRouteChallenges = routeChallenges.filter((c) => !c.ended && c.displayStartDateTime < new Date());
      for (const challenge of activeRouteChallenges) {
        try {
          const json = await backend.get('/coach-program/engagement/route-challenges/teams/my-team', {
            challengeId: challenge.id,
          });
          if (json) {
            const team = createRouteChallengeTeam(json);
            return {
              challenge,
              type: 'route',
              myProgress: team.totalCollectedPoints,
              totalGoal: challenge.totalPossiblePoints,
              rank: team.globalRank,
            };
          }
          return {
            challenge,
            type: 'route',
            myProgress: 0,
            totalGoal: challenge.totalPossiblePoints,
            rank: undefined,
          }
        } catch (err) {
          console.error('Error checking route challenge team:', err);
        }
      }

      return null;
    },
    enabled: !isLoadingIndividual && !isLoadingRoute,
    staleTime: 2 * 60 * 1000,
  });
}
